/**
 * Escala visual del panel administrativo (fuente única, 2026-10-07).
 *
 * El panel es una herramienta financiera, no una app de consumo: sobria, compacta y con poco radio.
 * `--radius` vale 0.5rem, así que en Tailwind `rounded-lg` = 8px, `rounded-md` = 6px y
 * `rounded-sm` = 4px. Lo "muy redondo" viene de clases sueltas (`rounded-xl`, `rounded-2xl`,
 * `rounded-full`), no del token; por eso la regla se aplica por componente y no cambiando `--radius`.
 *
 * Radios
 * - Controles (botón, input, select, segmentado, filtros por segmento): `rounded-md`.
 * - Tarjetas, paneles, secciones y elementos de lista con borde: `rounded-lg`. Nunca `rounded-xl/2xl`.
 * - Chips, badges y pastillas de estado: `rounded-md`. `rounded-full` solo para puntos indicadores.
 * - Avatares: `rounded-lg` (cuadrado suave). Círculo solo para avatares chicos en listas densas.
 * - Íconos de timeline o de tipo: cuadrado suave `rounded-md`, no círculo.
 *
 * Alturas
 * - Controles: h-8 (filtros, acciones secundarias, toolbars densas) o h-9 (formularios).
 * - Switch: tamaño "sm" (h-5) en paneles; el default (h-6) queda para formularios existentes.
 *
 * Tipografía
 * - Controles: 12 a 13px. Etiquetas de dato: 11px uppercase muted. Valores: 13px.
 * - Montos y cifras clave: `font-semibold tabular-nums`. Metadatos: 12px `text-muted-foreground`.
 *
 * Bordes y foco
 * - Borde neutro (`border-border`) por defecto. El borde de color primario se reserva para foco o
 *   selección real (p. ej. la cuenta abierta), y aun así sutil (`border-primary/30`).
 *
 * Los tokens de abajo son clases listas para componer con `cn()`.
 */

/** Tarjeta / panel / sección con borde. */
export const PANEL_CLS = "rounded-lg border border-border bg-card";

/** Elemento de lista con borde dentro de un panel (cuenta, promesa, ticket). */
export const ITEM_CLS = "rounded-lg border border-border";

/** Chip de dato o etiqueta (rectangular, compacto). Combinar con un tono de fondo/texto. */
export const CHIP_CLS = "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium leading-4";

/** Contador junto a un título o pestaña. */
export const COUNT_CLS = "rounded-md bg-muted px-1.5 text-[11px] font-medium tabular-nums text-muted-foreground";

/** Avatar de iniciales. `md` para cabecera de ficha, `sm` para listas. */
export const AVATAR_CLS = {
  md: "flex size-10 shrink-0 select-none items-center justify-center rounded-lg bg-primary/10 text-sm font-semibold text-primary",
  sm: "flex size-7 shrink-0 select-none items-center justify-center rounded-md bg-muted text-[11px] font-semibold text-muted-foreground",
} as const;

/** Ícono de evento o tipo dentro de un cuadrado suave (timeline, listas). */
export const ICON_TILE_CLS = "flex size-7 shrink-0 items-center justify-center rounded-md";

/** Etiqueta de dato (dt) y valor (dd). */
export const DATO_LABEL_CLS = "text-[11px] font-medium uppercase tracking-wide text-muted-foreground";
export const DATO_VALOR_CLS = "text-[13px] text-foreground";

/** Metadatos bajo un título (autor, fecha, cuenta). */
export const META_CLS = "text-xs text-muted-foreground";
